import React from 'react'
import { useCurrentFrame, useVideoConfig, interpolate } from 'remotion'
import { COLORS, FONTS, GLOW, GLASS } from '../shared/theme'
import { fadeIn, entrance, staggerDelay, breathe, pulse, SPRINGS } from '../shared/animations'
import SceneWrapper from '../shared/SceneWrapper'

/**
 * QuoteHighlight — Cita cinematográfica con comillas animadas y atribución.
 *
 * params: { quote, author, source, accent_color }
 */
export default function QuoteHighlight({ params = {}, artDirection = {} }) {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const { quote = '', author = '', source = '', accent_color = COLORS.accent.purple } = params

  const quoteMarkEntrance = entrance(frame, fps, 0, SPRINGS.bouncy)
  const quoteMarkOpacity = fadeIn(frame, 0, 20)
  const lineEntrance = entrance(frame, fps, 8, SPRINGS.gentle)
  const glowPulse = pulse(frame, 0.04, 0.5, 1.0)
  const authorOpacity = fadeIn(frame, 50, 20)
  const authorY = interpolate(frame, [50, 70], [16, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })

  // Split quote into lines of ~45 characters
  const words = quote.split(' ')
  const quoteLines = []
  let currentLine = ''
  for (const word of words) {
    if ((currentLine + ' ' + word).trim().length > 45 && currentLine) {
      quoteLines.push(currentLine.trim())
      currentLine = word
    } else {
      currentLine = currentLine ? currentLine + ' ' + word : word
    }
  }
  if (currentLine) quoteLines.push(currentLine.trim())

  const quoteFontSize = quoteLines.length <= 2 ? 58 : quoteLines.length <= 3 ? 48 : 40
  const lineBreath = breathe(frame, 0.018, 0.004)

  return (
    <SceneWrapper accent={accent_color} texture={artDirection?.texture}>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 160px',
        }}
      >
        {/* Container */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: 1280,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Opening quote mark */}
          <div
            style={{
              position: 'absolute',
              top: -80,
              left: -40,
              fontFamily: FONTS.heading,
              fontSize: 200,
              lineHeight: 1,
              color: accent_color,
              opacity: quoteMarkOpacity * 0.18,
              transform: `scale(${quoteMarkEntrance})`,
              transformOrigin: 'bottom right',
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          >
            &ldquo;
          </div>

          {/* Decorative top line */}
          <div
            style={{
              width: interpolate(lineEntrance, [0, 1], [0, 120]),
              height: 2,
              background: `linear-gradient(90deg, transparent, ${accent_color}, transparent)`,
              marginBottom: 40,
              boxShadow: GLOW.sm(accent_color),
            }}
          />

          {/* Quote text — line by line */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              textAlign: 'center',
              transform: `scale(${lineBreath})`,
            }}
          >
            {quoteLines.map((line, i) => {
              const delay = 20 + staggerDelay(i, 18)
              const lineOpacity = fadeIn(frame, delay, 20)
              const lineY = interpolate(frame, [delay, delay + 20], [20, 0], {
                extrapolateLeft: 'clamp',
                extrapolateRight: 'clamp',
              })
              return (
                <div
                  key={i}
                  style={{
                    opacity: lineOpacity,
                    transform: `translateY(${lineY}px)`,
                    fontFamily: FONTS.heading,
                    fontSize: quoteFontSize,
                    fontWeight: 700,
                    color: COLORS.text.primary,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                    textShadow: i === 0 ? `0 0 40px ${accent_color}25` : 'none',
                  }}
                >
                  {line}
                </div>
              )
            })}
          </div>

          {/* Closing quote mark */}
          <div
            style={{
              position: 'absolute',
              bottom: -60,
              right: -40,
              fontFamily: FONTS.heading,
              fontSize: 200,
              lineHeight: 1,
              color: accent_color,
              opacity: quoteMarkOpacity * 0.18,
              transform: `scale(${quoteMarkEntrance})`,
              transformOrigin: 'top left',
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          >
            &rdquo;
          </div>

          {/* Separator + attribution */}
          <div
            style={{
              opacity: authorOpacity,
              transform: `translateY(${authorY}px)`,
              marginTop: 56,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
            }}
          >
            {/* Horizontal rule */}
            <div
              style={{
                width: 60,
                height: 1,
                background: `rgba(255,255,255,0.12)`,
                marginBottom: 6,
              }}
            />

            {/* Author dot + name */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: accent_color,
                  boxShadow: GLOW.sm(accent_color),
                  opacity: glowPulse,
                }}
              />
              <div
                style={{
                  fontFamily: FONTS.heading,
                  fontSize: 22,
                  fontWeight: 700,
                  color: COLORS.text.secondary,
                  letterSpacing: '0.02em',
                }}
              >
                {author}
              </div>
            </div>

            {source ? (
              <div
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 15,
                  color: COLORS.text.muted,
                  letterSpacing: '0.04em',
                  fontStyle: 'italic',
                }}
              >
                {source}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </SceneWrapper>
  )
}
